'use client';

import { ReactNode, useState, useEffect, useRef } from 'react';

interface ToggleButtonProps {
  children: ReactNode;
  isSelected?: boolean;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

/**
 * ToggleButton - A button component with hover fade effect to yellow and text slide animation
 * 
 * @component
 * @example
 * ```tsx
 * <ToggleButton 
 *   isSelected={selectedOptions.includes('option')}
 *   onClick={() => toggleOption('option')}
 * >
 *   Option Text
 * </ToggleButton>
 * ```
 */
export default function ToggleButton({
  children,
  isSelected = false,
  onClick,
  className = '',
  style,
  disabled = false,
}: ToggleButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoverDisabled, setHoverDisabled] = useState(false);
  const prevSelectedRef = useRef(isSelected);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // When deselected (transitioning from selected to deselected), immediately disable hover, clear background
  useEffect(() => {
    // Only disable hover when transitioning FROM selected TO deselected (not on initial load)
    if (!isSelected && prevSelectedRef.current && buttonRef.current) {
      // Immediately disable hover and clear background
      setIsHovered(false);
      setHoverDisabled(true);
      // Force remove inline backgroundColor immediately
      requestAnimationFrame(() => {
        if (buttonRef.current) {
          buttonRef.current.style.backgroundColor = '';
        }
      });
    }
    // Update previous selected state
    prevSelectedRef.current = isSelected;
  }, [isSelected]);
  
  const baseClasses = 'px-4 py-1 rounded-full body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-colors duration-500 ease-out';
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  
  // Base styles - hover only works when not selected, not on mobile, and hover is not disabled
  // On desktop, use hover classes; on mobile, no hover
  const baseUnselectedClasses = isMobile || hoverDisabled
    ? 'bg-[#FDFEF6] text-[#666666] border border-[#666666]'
    : 'bg-[#FDFEF6] text-[#666666] border border-[#666666] hover:bg-[#FFDC32] hover:text-black hover:border-black';
  const selectedClasses = 'bg-[#FFDC32] text-black border border-gray-900';

  const combinedClasses = `${baseClasses} ${disabledClasses} ${isSelected ? selectedClasses : baseUnselectedClasses} ${className}`;
  
  // When selected: force yellow background via inline style
  // When deselected: NO inline backgroundColor at all - let CSS classes handle everything
  const buttonStyle: React.CSSProperties = isSelected
    ? {
        willChange: 'background-color, border-color, color',
        backgroundColor: '#FFDC32',
        ...style
      }
    : {
        willChange: 'background-color, border-color, color',
        // Explicitly exclude backgroundColor to let CSS classes handle it
        ...(style ? (() => {
          const { backgroundColor, color, borderColor, ...restStyle } = style;
          return restStyle;
        })() : {})
      };

  const content = (
    <div className="relative overflow-hidden px-2">
      <span 
        className={`block transition-transform duration-300 ease-out ${
          isSelected ? '-translate-y-full' : 'translate-y-0'
        }`}
      >
        {children}
      </span>
      <span 
        className={`absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-out ${
          isSelected ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {children}
      </span>
    </div>
  );

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      disabled={disabled}
      className={combinedClasses}
      style={buttonStyle}
      onMouseEnter={() => {
        if (!isSelected && !disabled && !hoverDisabled) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        if (!isSelected && !disabled) {
          setIsHovered(false);
          // Re-enable hover when mouse leaves after deselection
          if (hoverDisabled) {
            setHoverDisabled(false);
          }
        }
      }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled && onClick) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {content}
    </button>
  );
}

